/**
 * Lazy Loading Components
 * Performance-optimized components with lazy loading
 */

import React, { Suspense, lazy } from 'react';
import { Skeleton, Card, CardContent } from '@xanectech/ui/components';

import { LucideProps } from 'lucide-react';
import { createLogger } from '@xanectech/lib';

const logger = createLogger('Components - lazy-components');

type QrScannerProps = Record<string, unknown> & {
  onScan?: (result: unknown) => void;
};

type QrScannerComponent = React.ComponentType<QrScannerProps>;

const EmptyQrScanner: QrScannerComponent = () => (
  <div className='flex items-center justify-center p-8 text-muted-foreground'>
    <p>QR Scanner unavailable</p>
  </div>
);

// Lazy load heavy components with error handling
const LazyQRScanner = lazy(() =>
  import('@yudiel/react-qr-scanner')
    .then((module) => ({
      default: module.Scanner as QrScannerComponent,
    }))
    .catch((error: unknown) => {
      logger.error('Failed to load QR scanner:', {
        error: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
      });
      return {
        default: EmptyQrScanner,
      };
    }),
);

const LazyChart = lazy(() =>
  import('recharts').then((module) => ({
    default: module.ResponsiveContainer,
  })),
);

// Loading fallbacks
const QRScannerSkeleton = () => (
  <Card className='w-full max-w-sm mx-auto'>
    <CardContent className='p-6'>
      <div className='aspect-square bg-muted rounded-lg flex items-center justify-center'>
        <div className='text-muted-foreground'>Loading scanner...</div>
      </div>
    </CardContent>
  </Card>
);

const ChartSkeleton = () => (
  <div className='h-64 w-full'>
    <Skeleton className='h-full w-full rounded-lg' />
  </div>
);

// Optimized QR Scanner wrapper - pass through all props
const OptimizedQRScanner: React.FC<QrScannerProps> = (props) => {
  return (
    <Suspense fallback={<QRScannerSkeleton />}>
      <LazyQRScanner onScan={() => {}} {...props} />
    </Suspense>
  );
};

// Optimized Chart wrapper
interface OptimizedChartProps {
  children: React.ReactNode;
  width?: number | `${number}%`;
  height?: number | `${number}%`;
}

const OptimizedChart: React.FC<OptimizedChartProps> = ({
  children,
  width,
  height,
}) => {
  // Only pass width/height if they are valid types for ResponsiveContainer
  const validWidth =
    typeof width === 'number' ||
    (typeof width === 'string' && /^\d+%$/.test(width))
      ? width
      : undefined;
  const validHeight =
    typeof height === 'number' ||
    (typeof height === 'string' && /^\d+%$/.test(height))
      ? height
      : undefined;

  return (
    <Suspense fallback={<ChartSkeleton />}>
      <LazyChart width={validWidth} height={validHeight}>
        <div>{children}</div>
      </LazyChart>
    </Suspense>
  );
};

// Icon lazy loading with cache
const iconCache = new Map<string, React.ComponentType<LucideProps>>();

const LazyIcon: React.FC<{
  iconName: string;
  size?: number;
  className?: string;
}> = ({ iconName, size = 24, className }) => {
  const [icon, setIcon] = React.useState<
    React.ComponentType<LucideProps> | undefined
  >(iconCache.get(iconName));

  React.useEffect(() => {
    if (!icon && iconName) {
      import('lucide-react')
        .then((icons) => {
          const iconsRecord = icons as unknown as Record<
            string,
            React.ComponentType<LucideProps>
          >;
          const IconComponent = iconsRecord[iconName];
          if (IconComponent) {
            iconCache.set(iconName, IconComponent);
            setIcon(IconComponent);
          }
        })
        .catch((error: unknown) => {
          logger.error('Failed to load icon:', {
            error: error instanceof Error ? error.message : String(error),
            stack: error instanceof Error ? error.stack : undefined,
          });
          // Fallback for missing icons
          void import('lucide-react').then((icons) => {
            iconCache.set(iconName, icons.HelpCircle);
            setIcon(icons.HelpCircle);
          });
        });
    }
  }, [iconName, icon]);

  if (!icon) {
    return (
      <Skeleton
        className={`h-${Math.ceil(size / 4)} w-${Math.ceil(
          size / 4,
        )} ${className}`}
      />
    );
  }

  const IconComponent = icon;
  return <IconComponent size={size} className={className} />;
};

export {
  LazyQRScanner,
  LazyChart,
  QRScannerSkeleton,
  ChartSkeleton,
  type OptimizedChartProps,
  OptimizedChart,
  OptimizedQRScanner,
  LazyIcon,
};
